import React, { useContext } from 'react'
import { Modal, Text, View } from 'oio-react'
import Helmet from 'react-helmet'
import { Switch, useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import uiConstants, { LAYOUT_BOX, LAYOUT_HEADER } from 'config/constants/ui'
import { useOrganization } from 'src/core/graphql/hooks'
import InitiativeMoreButton from 'src/sites/kits/Initiative/components/MoreButton'
import MediaListBlock from 'src/sites/kits/Media/components/ListBlock'
import ObjectStatusBanner from 'src/sites/kits/Object/components/StatusBanner'
import ContentView from 'src/sites/kits/UI/Content/View'
import { Footer } from 'src/sites/kits/UI'
import VisualEditor from 'src/sites/kits/Object/components/VisualEditor'
import ObjectSettings from 'src/sites/kits/ObjectSettings'
import ModalRoute from 'src/sites/kits/Utils/ModalRoute'
import { InitiativeHierarchyContext, withInitiativeHierarchyProvider }
   from 'src/sites/kits/Utils/InitiativeHierarchy'
import PageUIContext from 'src/sites/kits/Utils/PageUIContext'
import { useGlobalState } from 'src/sites/state'

// TODO: This name is not fully accurate since it is used for Custom as well as Media Pages
// Either rename the component or split it into two or get rid of media pages (ie. convert them
// dynamic list pages)
const CustomPage = () => {
   const match = useRouteMatch()
   const { theme } = useGlobalState()
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX
   const isHeaderGL = theme.tmpGlobalLayout === LAYOUT_HEADER

   const { initiative: page, initiativeLoading } = useContext(InitiativeHierarchyContext)
   const { organization } = useOrganization()
   const { currentUserCanEdit } = organization
   const isBleedingEdge = organization.gatherAppVersion === 6

   if (initiativeLoading) {
      return null
   }

   // GNS3 and Fuse are currently only communities that use pages with custom page components
   // See PR #701
   const isPartnerProgramPage = isBleedingEdge ||
      ['banff-pipeline-workshop', 'eyeq', 'fuse', 'gns3'].includes(organization.slug)

   const isEmptyPage = page.body.elements.length === 0
   const isMediaPage = page.pageFormat === 'media'

   return (
      <>
         <Helmet title={`${page.name} | ${organization.name}`} />
         <View
            position="relative"
            marginTop={(isMediaPage || !isPartnerProgramPage) && theme.viewportMarginTop}
            float="left"
            width="100%">
            <ObjectStatusBanner />
            {!isMediaPage && (
               <View
                  position="fixed"
                  bottom="0px"
                  right="0px"
                  paddingRight={theme.tmpViewportPadding}
                  margin={isBoxGL ? '30px' : '30px 0px'}
                  zIndex="2">
                  <InitiativeMoreButton
                     buttonRounded
                     buttonSize="sm"
                     buttonWidth="30px"
                     editUrl={`${match.url}/-/edit`}
                     popoverAnchorOriginVertical="bottom"
                     showInitiativeName
                  />
               </View>
            )}
            {isMediaPage && (
               <View
                  paddingLeft={isHeaderGL ? theme.tmpViewportPadding : '0px'}
                  paddingRight={theme.tmpViewportPadding}>
                  <View
                     position="relative"
                     paddingTop={isBoxGL && '10px[d] 30px[e-f]'}
                     paddingLeft={isBoxGL && theme.tmpAppOuterPaddingHorizontal}
                     paddingRight={isBoxGL && '30px[d-f]'}>
                     <View position="relative">
                        <MediaListBlock
                           contentPaddingLeft="0px"
                           contentPaddingRight="0px"
                           defaultViewMode={uiConstants.viewModes.GRID}
                           initiativeSlug={page.slug}
                           titleSize="lg"
                           titlebarSize="lg"
                        />
                     </View>
                  </View>
               </View>
            )}
            {isEmptyPage && (
               <View
                  width="100%"
                  padding="120px 30px"
                  textAlign="center"
                  backgroundColor={theme.tmpContentBackgroundColor}>
                  <Text size="2" color="#888">
                     This page currently has no contents.
                     {page.currentUserCanEdit && (
                        <>
                           <br />
                           Click the
                           <View
                              display="inline-flex"
                              justifyContent="center"
                              alignItems="center"
                              width="20px"
                              height="20px"
                              margin="2px 5px 0px 5px"
                              backgroundColor="#e5e5e5"
                              borderRadius="50%">
                              <MoreIcon width="12px" height="12px" />
                           </View>
                           button in the bottom right corner of the page to edit.
                        </>
                     )}
                  </Text>
               </View>
            )}
            <View
               float="left"
               width="100%"
               minHeight={isBoxGL ? '100vh' : '90vh'}
               backgroundColor={theme.tmpContentBackgroundColor}
               paddingHorizontal={!isPartnerProgramPage && '350px[f]'}
               paddingVertical={!isPartnerProgramPage && '30px[a-b] 60px[c-f]'}>
               <PageUIContext.Provider
                  value={{
                     contentMaxWidth: isPartnerProgramPage ? '100%' : '750px[a-e]',
                     contentPaddingLeft: !isPartnerProgramPage && `
                        ${theme.tmpViewportPaddingA}[a]
                        ${theme.tmpViewportPaddingB}[b]
                        ${theme.tmpViewportPaddingC}[c]
                     `,
                     contentPaddingRight: !isPartnerProgramPage && `
                        ${theme.tmpViewportPaddingA}[a]
                        ${theme.tmpViewportPaddingB}[b]
                        ${theme.tmpViewportPaddingC}[c]
                     `,
                     contentPaddingVertical: !isPartnerProgramPage && '12px'
                  }}>
                  <ContentView elements={page.body.elements} />
               </PageUIContext.Provider>
            </View>
         </View>
         {!isBleedingEdge && (
            <Footer
               {...isPartnerProgramPage && {
                  backgroundColor: '#000',
                  textColor: '#777'
               }}
               {...isBoxGL && {
                  paddingLeft: '20px[a-b] 30px[c-f]',
                  paddingRight: '20px[a-b] 30px[c-f]'
               }}
            />
         )}
         {!isMediaPage && currentUserCanEdit && (
            <Switch>
               <ModalRoute path={`${match.path}/-/edit`}>
                  {({ open, onCloseComplete, onCloseTrigger }) => (
                     <>
                        {/* TODO: This is a temporary hack to ensure the Visual Editor is not
                           displayed for GNS3 pages. See #993. */}
                        {organization.slug !== 'gns3' && (
                           <Modal
                              borderRadius="6px"
                              width="100%"
                              height="100%"
                              onCloseComplete={onCloseComplete}
                              onCloseTrigger={onCloseTrigger}
                              openAnimationName="fadeIn"
                              open={open}>
                              <VisualEditor returnUrl={match.url} />
                           </Modal>
                        )}
                        {organization.slug === 'gns3' && (
                           <ObjectSettings.Modal
                              height="100%[a-b] 400px[c-f]"
                              width="90%[a] 400px[b-f]"
                              onCloseComplete={onCloseComplete}
                              onCloseTrigger={onCloseTrigger}
                              open={open}>
                              <ObjectSettings.Editor
                                 elements={['slug']}
                                 onCancelButtonClick={onCloseTrigger}
                                 onUpdate={onCloseTrigger}
                                 tmpHidePrivacyControls
                              />
                           </ObjectSettings.Modal>
                        )}
                     </>
                  )}
               </ModalRoute>
            </Switch>
         )}
      </>
   )
}

export default withInitiativeHierarchyProvider({ slugParam: 'initiativeSlug' })(CustomPage)
